import { Tabs } from '@mantine/core';
import {
  IconClick,
  IconCoin,
  IconDatabase,
  IconLayoutDashboard,
  IconSpeakerphone,
  IconTargetArrow,
  IconUsers,
  IconWorld,
} from '@tabler/icons-react';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { DateTime } from 'luxon';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import EmailSendingHoldAlert from '../../../components/EmailSendingHoldAlert';
import ErrorMessage from '../../../components/ErrorMessage';
import Metadata from '../../../components/Metadata';
import MetricsSelector from '../../../components/MetricsSelector';
import Spinner from '../../../components/Spinner';
import WelcomeGuideTrigger from '../../../components/WelcomeGuideTrigger';
import { DashboardTabs, Page } from '../../../constants/pageTitles';
import { daysAgoISODate, dateToEndOfDay, dateToStartOfDay, todayISODate } from '../../../helpers/dateUtils';
import useFetch from '../../../helpers/useFetch';
import AtAGlanceTab from './AtAGlanceTab';
import DataQualityTab from './DataQualityTab';
import ConversionTab from './ConversionTab';
import EngagementTab from './EngagementTab';
import MarketingTab from './MarketingTab';
import RevenueTab from './RevenueTab';
import TrafficTab from './TrafficTab';
import UserBaseTab from './UserBaseTab';

const mainTabs = [
  'atAGlance',
  'traffic',
  'engagement',
  'conversion',
  'revenue',
  'userBase',
  'marketing',
  'dataQuality',
];

const statsDebounced = AwesomeDebouncePromise((fetch, accountId, domainId, startTs, endTs) => {
  return fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/stats/${encodeURIComponent(startTs)}/${encodeURIComponent(endTs)}`);
}, 300);

const prevStatsDebounced = AwesomeDebouncePromise((fetch, accountId, domainId, startTs, endTs) => {
  return fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/stats/${encodeURIComponent(startTs)}/${encodeURIComponent(endTs)}`);
}, 300);

const customerTypeStatsDebounced = AwesomeDebouncePromise((fetch, accountId, domainId, startTs, endTs) => {
  return fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/stats/purchasesByCustomerType/${encodeURIComponent(startTs)}/${encodeURIComponent(endTs)}`);
}, 300);

const DashboardPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const fetch = useFetch();

  const { domain } = useSelector((state) => state.domain);

  const initialPeriodDays = 14;
  const search = queryString.parse(location.search);
  const mainTab = search.mainTab || mainTabs[0];
  // At a Glance is the tab everyone lands on and it consumes none of these
  // payloads (it self-fetches), so skipping them keeps the first paint off the
  // heavy /stats queries. A boolean rather than mainTab itself keeps switching
  // between the other seven tabs from refetching, as today.
  const needsStats = mainTab !== 'atAGlance';
  const [stats, setStats] = useState();
  const [prevStats, setPrevStats] = useState();
  const [customerTypeStats, setCustomerTypeStats] = useState();
  const [error, setError] = useState();
  // Store ISO date strings (YYYY-MM-DD)
  const [startDate, setStartDate] = useState(daysAgoISODate(initialPeriodDays - 1));
  const [endDate, setEndDate] = useState(todayISODate());

  const setMainTab = (mainTab) => {
    navigate(
      `${location.pathname}?${queryString.stringify({
        ...search,
        mainTab,
      })}`
    );
  };

  // Calculate the previous period based on ISO date strings
  const calculatePrevPeriod = (startISODate, endISODate) => {
    const startDt = DateTime.fromISO(startISODate);
    const endDt = DateTime.fromISO(endISODate);
    const diffDays = endDt.diff(startDt, 'days').days;

    const prevEndDt = startDt.minus({ days: 1 });
    const prevStartDt = prevEndDt.minus({ days: diffDays });

    return {
      start: prevStartDt.toISODate(),
      end: prevEndDt.toISODate(),
    };
  };

  useEffect(() => {
    setStats(undefined);
    setPrevStats(undefined);
    setCustomerTypeStats(undefined);

    if (!needsStats) {
      return;
    }

    // Convert ISO date strings to full timestamps for the API (using local timezone)
    const startTs = dateToStartOfDay(startDate, 'local');
    const endTs = dateToEndOfDay(endDate, 'local');

    statsDebounced(fetch, domain.accountId, domain.id, startTs, endTs).then(setStats).catch(setError);
    customerTypeStatsDebounced(fetch, domain.accountId, domain.id, startTs, endTs).then(setCustomerTypeStats).catch(setError);

    const prevPeriod = calculatePrevPeriod(startDate, endDate);
    const prevStartTs = dateToStartOfDay(prevPeriod.start, 'local');
    const prevEndTs = dateToEndOfDay(prevPeriod.end, 'local');

    prevStatsDebounced(fetch, domain.accountId, domain.id, prevStartTs, prevEndTs).then(setPrevStats).catch(setError);
  }, [domain.accountId, domain.id, startDate, endDate, fetch, needsStats]);

  // Both the At a Glance tab and the Conversion tab need the comparison range.
  const previousPeriod = calculatePrevPeriod(startDate, endDate);

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  if (needsStats && (stats === undefined || prevStats === undefined)) {
    return <Spinner />;
  }

  return (
    <div>
      <WelcomeGuideTrigger />
      <Metadata title={`${Page['dashboard']} | ${DashboardTabs[mainTab]}`} />
      <MetricsSelector
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        rowSize="12"
      />

      {!needsStats || stats ? (
        <>
          <EmailSendingHoldAlert accountId={domain.accountId} domainId={domain.id} />
          <Tabs value={mainTab} onChange={setMainTab} mb={0} keepMounted={false} mt="md">
            <Tabs.List>
              <Tabs.Tab value="atAGlance" leftSection={<IconLayoutDashboard size={16} />}>
                At a Glance
              </Tabs.Tab>
              <Tabs.Tab value="traffic" data-tour="trafficTab" leftSection={<IconWorld size={16} />}>
                Traffic
              </Tabs.Tab>
              <Tabs.Tab value="engagement" leftSection={<IconClick size={16} />}>
                Engagement
              </Tabs.Tab>
              <Tabs.Tab value="conversion" leftSection={<IconTargetArrow size={16} />}>
                Conversion
              </Tabs.Tab>
              <Tabs.Tab value="revenue" data-tour="revenueTab" leftSection={<IconCoin size={16} />}>
                Revenue
              </Tabs.Tab>
              <Tabs.Tab value="userBase" data-tour="userBaseTab" leftSection={<IconUsers size={16} />}>
                User Base
              </Tabs.Tab>
              <Tabs.Tab value="marketing" data-tour="marketingTab" leftSection={<IconSpeakerphone size={16} />}>
                Marketing
              </Tabs.Tab>
              <Tabs.Tab value="dataQuality" leftSection={<IconDatabase size={16} />}>
                Data Quality
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="atAGlance">
              <AtAGlanceTab
                startDate={startDate}
                endDate={endDate}
                prevStartDate={previousPeriod.start}
                prevEndDate={previousPeriod.end}
              />
            </Tabs.Panel>
            <Tabs.Panel value="traffic">
              <TrafficTab stats={stats} prevStats={prevStats} />
            </Tabs.Panel>
            <Tabs.Panel value="engagement">
              <EngagementTab stats={stats} prevStats={prevStats} />
            </Tabs.Panel>
            <Tabs.Panel value="conversion">
              <ConversionTab
                stats={stats}
                prevStats={prevStats}
                periods={{
                  current: { start: startDate, end: endDate },
                  prev: previousPeriod,
                }}
              />
            </Tabs.Panel>
            <Tabs.Panel value="revenue">
              <RevenueTab currency={domain.currency} stats={stats} prevStats={prevStats} customerTypeStats={customerTypeStats} />
            </Tabs.Panel>
            <Tabs.Panel value="userBase">
              <UserBaseTab stats={stats} prevStats={prevStats} />
            </Tabs.Panel>
            <Tabs.Panel value="marketing">
              <MarketingTab stats={stats} prevStats={prevStats} />
            </Tabs.Panel>
            <Tabs.Panel value="dataQuality">
              <DataQualityTab startDate={startDate} endDate={endDate} />
            </Tabs.Panel>
          </Tabs>
        </>
      ) : (
        <Spinner />
      )}
    </div>
  );
};

export default DashboardPage;
